/**
 * contextoProcessualHelper.js
 * 
 * Utilitário compartilhado entre ProcurationEngine e ContractEngine
 * para geração automática do Contexto Processual Inteligente.
 * 
 * Criado em: 15/04/2026 — Sprint Simbiose Contrato ↔ Procuração
 */

import { escapeHtml } from "./escapeHtml";

/**
 * Gera a frase de contexto processual dinâmico.
 * 
 * @param {Object} config
 * @param {string} config.parteContrariaName - Nome da parte adversa
 * @param {string[]} config.posicoes - Array de posições do cliente (ex: ['Autor', 'Requerente'])
 * @param {string[]} config.ramos - Array de ramos do direito ativos (ex: ['Civil', 'Penal'])
 * @param {'procuracao'|'contrato'} config.documentType - Tipo de documento
 * @returns {string} Frase HTML do contexto processual (vazio se parte contrária não informada)
 */
export function buildContextoProcessual({ parteContrariaName, posicoes = [], ramos = [], documentType = 'procuracao' }) {
    const pc = String(parteContrariaName || '').trim();
    if (!pc || pc === '...') return '';

    const pcBold = `<strong>${escapeHtml(pc.toUpperCase())}</strong>`;

    // Rótulo da parte principal conforme documento
    const isProcuracao = documentType === 'procuracao';
    const parteLabel = isProcuracao ? '<strong>OUTORGANTE</strong>' : '<strong>CONTRATANTE</strong>';
    const abertura = isProcuracao ? 'A presente outorga visa' : 'O presente contrato tem por objeto';

    const roles = (Array.isArray(posicoes) ? posicoes : [])
        .map((r) => String(r || '').trim().toUpperCase()).filter(Boolean);
    const role = roles[0] || '';
    const tokens = roles.flatMap((r) => r.split('/').map((t) => t.trim()).filter(Boolean));
    const has = (keyword) => tokens.some((t) => t.includes(keyword)) || roles.some((r) => r.includes(keyword));

    const ramoKeys = Array.isArray(ramos) ? ramos : [];
    const isPenal = ramoKeys.includes('Penal');
    const isTrabalhista = ramoKeys.includes('Trabalho');
    const isPrevidenciario = ramoKeys.includes('Previdenciario');
    const isAdministrativo = ramoKeys.includes('Administrativo');
    const isFamilia = ramoKeys.includes('Familia');
    const isSocietario = ramoKeys.includes('SocietarioEmpresarial');

    const isPenalPubAtivo = isPenal && (has('ASSISTENTE') || /V[ÍI]TIMA|OFENDIDO/i.test(role));
    const isPenalPubPassivo = isPenal && (has('INVESTIGADO') || has('INDICIADO') || /R[ÉE]U.*ACUSADO|ACUSADO/i.test(role));
    const isPenalPrivAtivo = isPenal && has('QUERELANTE');
    const isPenalPrivPassivo = isPenal && has('QUERELADO');

    if (isPenalPrivAtivo) return `${abertura} o oferecimento de queixa-crime (ação penal de iniciativa privada) em face de ${pcBold}, nos termos do Art. 44 do CPP.`;
    if (isPenalPrivPassivo) return `${abertura} a defesa da parte ${parteLabel} na queixa-crime (ação penal de iniciativa privada) oferecida por ${pcBold}.`;
    if (isPenalPubAtivo) return `${abertura} a representação dos interesses da parte ${parteLabel}, na qualidade de vítima/assistente de acusação, na persecução penal de iniciativa pública que envolve ${pcBold}.`;
    if (isPenalPubPassivo) return `${abertura} a defesa da parte ${parteLabel} na ação penal de iniciativa pública, no procedimento que envolve ${pcBold}.`;
    if (isPenal) return `${abertura} a atuação da parte ${parteLabel} no procedimento criminal que envolve ${pcBold}.`;
    if (isTrabalhista && has('RECLAMANTE')) return `${abertura} a propositura de reclamação trabalhista em face de ${pcBold}.`;
    if (isTrabalhista && has('RECLAMADA')) return `${abertura} a defesa da parte ${parteLabel} na reclamação trabalhista movida por ${pcBold}.`;
    if (isTrabalhista && has('RESPONSÁVEL')) return `${abertura} a atuação da parte ${parteLabel}, na qualidade de responsável solidário/subsidiário, na reclamação trabalhista que envolve ${pcBold}.`;
    if (isTrabalhista) return `${abertura} a atuação da parte ${parteLabel} na demanda trabalhista que envolve ${pcBold}.`;
    if (isPrevidenciario && has('REQUERENTE')) return `${abertura} a propositura de ação previdenciária em face de ${pcBold}.`;
    if (isPrevidenciario) return `${abertura} a defesa da parte ${parteLabel} na ação previdenciária movida por ${pcBold}.`;
    if (isAdministrativo && has('IMPETRANTE')) return `${abertura} a impetração de mandado de segurança ou medida administrativa contra ${pcBold}.`;
    if (isAdministrativo) return `${abertura} a defesa da parte ${parteLabel} no procedimento administrativo instaurado por ${pcBold}.`;
    if (isFamilia && (has('AUTOR') || has('REQUERENTE'))) return `${abertura} a propositura de ação de família em face de ${pcBold}.`;
    if (isFamilia) return `${abertura} a defesa dos interesses da parte ${parteLabel} na ação de família movida por ${pcBold}.`;
    if (isSocietario) return `${abertura} a atuação nos interesses societários e empresariais da parte ${parteLabel} na demanda que envolve ${pcBold}.`;
    if (has('EXEQUENTE')) return `${abertura} a promoção de execução judicial em face de ${pcBold}.`;
    if (has('EXECUTADO')) return `${abertura} a defesa da parte ${parteLabel} na execução promovida por ${pcBold}.`;
    if (has('LITISCONSORTE') || has('TERCEIRO')) return `${abertura} a atuação da parte ${parteLabel} como terceiro interessado na demanda que envolve ${pcBold}.`;
    if (has('FIADOR') || has('AVALISTA')) return `${abertura} a atuação da parte ${parteLabel}, na qualidade de fiador/avalista, na demanda que envolve ${pcBold}.`;
    if (has('AUTOR') || has('REQUERENTE')) return `${abertura} a propositura de ação em face de ${pcBold}.`;
    return `${abertura} a defesa dos interesses da parte ${parteLabel} na demanda movida por ${pcBold}.`;
}
